/**
 * Opportunity API — Connect RPC implementation
 * Calls MorgansService via sabaody gateway.
 * Returns the same types as the REST opportunityApi.
 */ import { morgansClient } from '../connect/clients';
/** Convert a proto Timestamp to an ISO date string, or null if not set */ function timestampToString(ts) {
    if (!ts) return null;
    return new Date(Number(ts.seconds) * 1000 + ts.nanos / 1000000).toISOString();
}
/** Map proto Opportunity to frontend Opportunity type */ function mapOpportunity(proto) {
    return {
        _id: proto.id,
        noticeId: proto.noticeId,
        title: proto.title,
        organization: {
            name: proto.fullAgencyHierarchy || proto.department || ''
        },
        postedDate: timestampToString(proto.postedDate) ?? '',
        responseDeadLine: timestampToString(proto.responseDeadline),
        typeOfSetAsideDescription: proto.typeOfSetAsideDescription || null,
        naicsCodes: proto.naicsCodes,
        description: proto.description,
        descriptionLink: proto.descriptionLink || proto.additionalInfoLink || null,
        uiLink: proto.uiLink,
        fullAgencyHierarchy: proto.fullAgencyHierarchy,
        hasAttachments: proto.hasAttachments,
        attachmentCount: proto.attachmentCount,
        summary: proto.summary || undefined,
        scrapeSource: proto.scrapeSource || undefined,
        isSaved: proto.isSaved,
        isAlerted: proto.isAlerted,
        savedNotes: proto.savedNotes || undefined
    };
}
export const opportunityApiConnect = {
    async listOpportunities (params = {}) {
        const response = await morgansClient.listOpportunities({
            limit: params.limit ?? 0,
            offset: params.offset ?? 0,
            search: params.search ?? '',
            agency: params.agency ?? '',
            setAsideType: params.setAsideType ?? '',
            naicsCode: params.naicsCode ?? '',
            postedAfter: params.postedAfter ?? '',
            deadlineAfter: params.deadlineAfter ?? '',
            deadlineBefore: params.deadlineBefore ?? '',
            source: params.source ?? '',
            savedOnly: params.saved ?? false,
            // Show only active by default; include inactive when viewing saved
            activeOnly: !params.saved
        });
        return {
            opportunities: response.opportunities.map(mapOpportunity),
            total: Number(response.total),
            limit: response.limit,
            offset: response.offset,
            hasMore: response.hasMore,
            totalPages: response.totalPages
        };
    },
    async getOpportunity (noticeId) {
        const response = await morgansClient.getOpportunity({
            noticeId
        });
        if (!response.opportunity) {
            throw new Error('Opportunity not found');
        }
        return mapOpportunity(response.opportunity);
    },
    async getSimilarOpportunities (noticeId, limit) {
        const response = await morgansClient.getSimilarOpportunities({
            noticeId,
            limit: limit ?? 10
        });
        // Flatten proto SimilarOpportunity via mapOpportunity for field consistency
        const opportunities = response.opportunities.filter((s)=>s.opportunity != null).map((s)=>({
                ...mapOpportunity(s.opportunity),
                score: s.score,
                timeline: s.timeline.map((t)=>({
                        _id: t.noticeId,
                        noticeId: t.noticeId,
                        amendmentCount: t.amendmentCount,
                        postedDate: timestampToString(t.postedDate) ?? '',
                        modifiedDate: timestampToString(t.modifiedDate) ?? undefined,
                        uiLink: t.uiLink
                    }))
            }));
        return {
            opportunities,
            total: response.totalCandidates
        };
    },
    async getOpportunityHistory (noticeId) {
        const response = await morgansClient.getOpportunityHistory({
            noticeId
        });
        if (response.versions.length === 0) {
            return {
                entries: [],
                solicitationNumber: '',
                total: 0
            };
        }
        const maxAmendment = response.versions.reduce((max, v)=>Math.max(max, v.amendmentCount), -1);
        return {
            entries: response.versions.map((v)=>({
                    noticeId: v.noticeId,
                    amendmentCount: v.amendmentCount,
                    modifiedDate: timestampToString(v.modifiedDate),
                    postedDate: timestampToString(v.postedDate) ?? '',
                    changes: v.changeDescription || '',
                    attachments: [],
                    uiLink: v.uiLink,
                    isCurrent: v.amendmentCount === maxAmendment
                })),
            solicitationNumber: response.versions[0]?.solicitationNumber ?? '',
            total: response.total
        };
    }
};
